import { memo, useEffect, useState } from 'react';

import { Input } from '@/shadcn/input';

import {
  hasValueLimit,
  MAX_FILTER_VALUES
} from '@/components/ui/table/filters/filters.limit';
import type { TableFilterProps } from '@/components/ui/table/filters/filters.type';

const DEBOUNCE_TIME = 1500;

const countValues = (input: string) =>
  input.split(',').filter(value => value.trim() !== '').length;

const limitMessage = (
  refusedCount: number | undefined,
  appliedCount: number
): string | undefined => {
  if (refusedCount !== undefined) {
    return `Too many values: ${refusedCount} (maximum ${MAX_FILTER_VALUES})`;
  }
  if (appliedCount > MAX_FILTER_VALUES) {
    return `Too many values: ${appliedCount} (maximum ${MAX_FILTER_VALUES}). Only the first ${MAX_FILTER_VALUES} are used.`;
  }
  return;
};

// text filter
export const TextFilter = memo(
  ({
    colConfig,
    setColumnFilter,
    filterConfig,
    serverSideFiltering = false
  }: TableFilterProps<'text'>) => {
    // get current filter string value
    const filterStr = colConfig.filter ? colConfig.filter.value : '';

    const [inputStr, setInputStr] = useState<Maybe<string>>(filterStr);
    const [refusedCount, setRefusedCount] = useState<number>();

    const limited =
      serverSideFiltering && hasValueLimit(filterConfig.interpreter);
    const valueCount = countValues(inputStr ?? '');
    const message = limited
      ? limitMessage(refusedCount, countValues(filterStr ?? ''))
      : undefined;

    // debounce update of the column filter
    useEffect(() => {
      if (inputStr === filterStr) return;

      const handler = setTimeout(() => {
        setColumnFilter(
          colConfig.key,
          inputStr === '' ? undefined : inputStr,
          filterConfig
        );
      }, DEBOUNCE_TIME);

      return () => {
        clearTimeout(handler);
      };
    }, [inputStr]);

    const handleChange = (value: string) => {
      const count = countValues(value);
      if (limited && count > MAX_FILTER_VALUES && count > valueCount) {
        setRefusedCount(count);
        return;
      }
      setRefusedCount(undefined);
      setInputStr(value);
    };

    return (
      <>
        <Input
          aria-invalid={message !== undefined}
          className="h-9 w-full bg-card text-muted-foreground dark:bg-card"
          data-cy={`table-filter-text-${colConfig.key}`}
          id={filterConfig.path}
          onChange={e => handleChange(e.target.value)}
          placeholder={filterConfig.placeholder || 'Search'}
          type="search"
          value={inputStr}
        />
        {message && (
          <p className="text-destructive text-xs" role="alert">
            {message}
          </p>
        )}
      </>
    );
  }
);
